import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Container,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import PageHeader from '@components/shared/PageHeader';
import { useLegacySchemaInventoryQuery } from '../hooks/queries/useLegacySchemaQuery';

const relationColor = (kind?: string) => {
  switch (kind) {
    case 'user_root':
      return 'blue';
    case 'user_direct':
      return 'teal';
    case 'user_via_email':
      return 'cyan';
    case 'indirect':
      return 'grape';
    case 'fk_other':
      return 'orange';
    case 'no_user_relation':
      return 'gray';
    default:
      return 'gray';
  }
};

export default function AdminLegacySchema() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useLegacySchemaInventoryQuery();

  return (
    <Container size='2xl' py='lg'>
      <Stack gap='lg'>
        <PageHeader
          title='Legacy DB inventory (vapor)'
          subtitle='Every table + how it relates to users (or not).'
          breadcrumbs={[
            { label: 'Admin', href: '/admin/users' },
            { label: 'Legacy Migration', href: '/admin/legacy-migration' },
            { label: 'Schema' },
          ]}
          backUrl='/admin/legacy-migration'
          actions={
            <Badge color='yellow' variant='filled'>
              full schema
            </Badge>
          }
        />

        {isLoading ? (
          <Group justify='center' py='xl'>
            <Loader />
          </Group>
        ) : error ? (
          <Text c='red'>{(error as Error).message}</Text>
        ) : !data ? (
          <Text c='dimmed'>No inventory</Text>
        ) : (
          <Stack gap='md'>
            <Alert color='orange' title='Sparse data ≠ missing joins'>
              {data.note} On this DB, <strong>patients</strong> touch only ~
              7 users, <strong>subscriptions</strong> is empty (0 rows),{' '}
              <strong>payment_intents</strong> match ~1 user by email. Catalog
              tables (courses, videos, food, roles, …) have no user FK by
              design.
            </Alert>

            {data.no_user_relation_tables &&
            data.no_user_relation_tables.length > 0 ? (
              <Alert color='gray' title='Tables with NO user relation'>
                {data.no_user_relation_tables.join(', ')}
              </Alert>
            ) : null}

            <Group gap='md'>
              <Badge size='lg' variant='light'>
                DB: {data.database}
              </Badge>
              <Badge size='lg' variant='light'>
                Tables: {data.table_count}
              </Badge>
              <Badge size='lg' variant='light'>
                users: {data.users_count}
              </Badge>
            </Group>

            <Paper withBorder p='md' radius='md'>
              <Title order={4} mb='sm'>
                All tables
              </Title>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Table</Table.Th>
                    <Table.Th>Rows</Table.Th>
                    <Table.Th>Relation</Table.Th>
                    <Table.Th>Link via</Table.Th>
                    <Table.Th>Users with data</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.tables.map((table) => (
                    <Table.Tr
                      key={table.name}
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        navigate(
                          `/admin/legacy-users/schema/tables/${encodeURIComponent(table.name)}`
                        )
                      }
                    >
                      <Table.Td>
                        <Text fw={600}>{table.name}</Text>
                      </Table.Td>
                      <Table.Td>{table.row_count}</Table.Td>
                      <Table.Td>
                        <Badge
                          size='sm'
                          color={relationColor(table.relation_kind)}
                          variant='light'
                        >
                          {table.relation_kind ?? '—'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size='xs' c='dimmed'>
                          {table.link_via ?? '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {table.users_with_rows == null ? (
                          '—'
                        ) : (
                          <Text size='sm'>
                            {table.users_with_rows}
                            {table.users_coverage_pct != null
                              ? ` (${table.users_coverage_pct}%)`
                              : ''}
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
